import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  createRef,
} from 'react';
import {SUPPLEMENT_SECTIONS} from 'data/supplementSections';
import {Title, TitleLevel, TitleSize} from 'components/ui/Title';
import {useInView} from 'react-intersection-observer';
import SupplementTile from '../SupplementTile';
import debounce from 'lodash/debounce';
import useMediaQuery from 'hooks/useMediaQuery';
import Container from 'components/layout/Container';

import './Calculator.scss';

const Calculator = () => {
  const supplementSections = SUPPLEMENT_SECTIONS;
  const [currentSectionId, setCurrentSectionId] = useState(
    Object.keys(supplementSections)[0],
  );

  const refs = useMemo(() => {
    return Object.keys(supplementSections).reduce((refsObject, sectionId) => {
      refsObject[sectionId] = createRef();
      return refsObject;
    }, {});
  }, [supplementSections]);

  const handleTabClick = useCallback(
    sectionId => {
      refs[sectionId]?.current?.scrollIntoView({
        behavior: 'smooth',
      });
    },
    [refs],
  );

  const sectionIds = useMemo(() => {
    return Object.keys(supplementSections);
  }, [supplementSections]);

  const sections = useMemo(() => {
    return Object.values(supplementSections);
  }, [supplementSections]);

  return (
    <Container>
      <section className="calculator">
        <CalculatorTabs
          sectionIds={sectionIds}
          sections={sections}
          currentSectionId={currentSectionId}
          handleTabClick={handleTabClick}
        />

        <CaclulatorCategories
          sections={sections}
          currentSectionId={currentSectionId}
          setCurrentSectionId={setCurrentSectionId}
          refs={refs}
        />
      </section>
    </Container>
  );
};

const CalculatorTabs = ({
  sectionIds,
  sections,
  currentSectionId,
  handleTabClick,
}) => {
  const refs = useMemo(
    () =>
      sections.reduce((refsObject, section) => {
        refsObject[section.id] = createRef();
        return refsObject;
      }, {}),
    [sectionIds],
  );

  const onCurrentSectionIdChange = useCallback(
    debounce(
      currentSectionId =>
        refs[currentSectionId]?.current?.scrollIntoView({
          inline: 'center',
          block: 'nearest',
          behavior: 'smooth',
        }),
      1000,
    ),
    [],
  );

  useEffect(() => {
    onCurrentSectionIdChange(currentSectionId);
  }, [currentSectionId]);

  if (sections.length < 2) {
    return null;
  }

  return (
    <div className="calculator-tabs">
      {sections.map(section => (
        <p
          className={`calculator-tab ${
            section.id === currentSectionId ? 'calculator-tab_active' : ''
          }`}
          onClick={() => {
            handleTabClick(section.id);
          }}
          key={section.id}
          ref={refs[section.id]}
        >
          {section.name}
        </p>
      ))}
    </div>
  );
};

const CaclulatorCategories = ({
  sections,
  currentSectionId,
  setCurrentSectionId,
  refs,
}) => {
  const [observerMargin, setObserverMargin] = useState(0);

  useEffect(() => {
    function handleResize() {
      setObserverMargin(Math.floor(window.innerHeight / 2) - 1);
    }

    handleResize();

    document.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="calculator-categories">
      {sections.map(section => (
        <CalculatorSection
          key={section.id}
          section={section}
          currentSectionId={currentSectionId}
          setCurrentSectionId={setCurrentSectionId}
          supplements={section.supplements}
          observerMargin={observerMargin}
          innerRef={refs[section.id]}
        />
      ))}
    </div>
  );
};

const CalculatorSection = ({
  section,
  setCurrentSectionId,
  supplements,
  observerMargin,
  innerRef,
}) => {
  const [inViewRef, isInView] = useInView({
    rootMargin: `-${observerMargin}px 0px -${observerMargin}px 0px`,
    delay: 100,
  });
  const [isOpen, setIsOpen] = useState(false);

  const isMobile = useMediaQuery('(max-width: 1024px)');
  const numberOfSupplementsToShow = isMobile ? 5 : 12;
  const visibleSupplements = isOpen
    ? supplements
    : supplements.slice(0, numberOfSupplementsToShow);
  const canBeOpened = supplements.length > numberOfSupplementsToShow && !isOpen;

  const setRefs = useCallback(
    node => {
      innerRef.current = node;
      inViewRef(node);
    },
    [inViewRef],
  );

  const onIsInViewChange = useCallback(
    debounce(isInView => {
      if (isInView) {
        setCurrentSectionId(section.id);
      }
    }, 200),
    [],
  );

  useEffect(() => {
    onIsInViewChange(isInView);
  }, [isInView]);

  return (
    <div className="calculator-category" ref={setRefs}>
      <Title
        extraClassName="calculator-category__title"
        level={TitleLevel.H3}
        size={TitleSize.H3}
      >
        {section.name}
      </Title>

      <div className="calculator-supplements">
        {visibleSupplements.map((supplement, index) => (
          <SupplementTile key={index} supplement={supplement} />
        ))}
      </div>

      {canBeOpened && (
        <div
          className="calculator-supplements-button_open"
          onClick={() => setIsOpen(true)}
        >
          Ещё {supplements.length - visibleSupplements.length} услуг
        </div>
      )}
    </div>
  );
};

export default Calculator;
